import { Button, Checkbox, Col, Form, Input, InputNumber, Row, Select, Tag } from 'antd';
import { WrappedFormUtils } from 'antd/lib/form/Form';
import React, { useState } from 'react';
import { OrderHistoryItem } from '../../../../../core-module/services/logistics/types';
import { TextAreaWithCharacterCount } from '../../../../../components/TextAreaWithCharacterCount';
import { SelectableDeliveryMoment } from '../../../../../core-module/selectors/Order/types';

const dishLayout = {
  wrapperCol: {
    xs: { span: 24, offset: 0 }
  }
};

interface Props {
  readonly form: WrappedFormUtils;
  readonly selectableDeliveryMoments: ReadonlyArray<SelectableDeliveryMoment>;
  readonly selectedDeliveryMoment: SelectableDeliveryMoment | null;
  readonly isSubmitting: boolean;
  readonly existingOrder?: OrderHistoryItem;
  readonly customerDefaultPortionId: number | null;
  readonly onSaveClick: () => void;
  readonly onHideClick: () => void;
  readonly onChangeDeliveryMoment: (deliveryId: number, deliveryMomentId: number) => void;
}

const OrderFormComponent: React.FC<Props> = ({
  form,
  selectableDeliveryMoments,
  selectedDeliveryMoment,
  isSubmitting,
  existingOrder,
  customerDefaultPortionId,
  onSaveClick,
  onHideClick,
  onChangeDeliveryMoment
}) => {
  const [defaultPortionId, setDefaultPortionId] = useState(
    existingOrder ? null : customerDefaultPortionId
  );

  const handleDeliveryMomentChange = (index: number) => {
    const deliveryMoment = selectableDeliveryMoments[index];
    onChangeDeliveryMoment(deliveryMoment.deliveryId, deliveryMoment.deliveryMomentId);
  };

  const findDeliveryMomentIndex = () => {
    return selectableDeliveryMoments.findIndex(
      (deliveryMoment) => deliveryMoment === selectedDeliveryMoment
    );
  };

  const deliveryMomentIndex = findDeliveryMomentIndex();

  return (
    <Form
      style={{ marginTop: 20 }}
      labelCol={{
        xs: { span: 24 },
        sm: { span: 4 }
      }}
      wrapperCol={{
        xs: { span: 24 },
        sm: { span: 10 }
      }}
    >
      <Row>
        <Col>
          <Button onClick={onHideClick}>Annuleren</Button>
          <Button
            style={{ float: 'right' }}
            disabled={selectedDeliveryMoment === null}
            loading={isSubmitting}
            type='primary'
            onClick={onSaveClick}
          >
            Opslaan
          </Button>
        </Col>
      </Row>
      <hr />
      <Row>
        <Col xs={12}>
          <Form.Item
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            label='Levermoment'
            extra='Dagen waar al een bestelling voor zijn zijn uitgeschakeld.'
          >
            {form.getFieldDecorator('delivery_date', {
              rules: [{ required: true, message: 'Kies een levermoment' }],
              initialValue: existingOrder ? deliveryMomentIndex : undefined
            })(
              <Select<number>
                disabled={existingOrder != null}
                placeholder='- Maak een keuze -'
                onChange={(index) => handleDeliveryMomentChange(index)}
              >
                {selectableDeliveryMoments.map((deliveryMoment, index) => (
                  <Select.Option key={index} value={index} disabled={!deliveryMoment.selectable}>
                    {`${deliveryMoment.deliveryDate.format('dd DD MMM')} tussen ${
                      deliveryMoment.deliveryTimeStart
                    } en ${
                      deliveryMoment.deliveryTimeEnd
                    } | Wk ${deliveryMoment.deliveryDate.isoWeek()}`}
                  </Select.Option>
                ))}
              </Select>
            )}
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item
            labelCol={{ span: 8 }}
            wrapperCol={{ span: 16 }}
            label='Routeopmerking'
            extra={
              <div>
                Plaats hier alleen een eenmalige opmerking mbt deze levering. Deze zal op de
                routelijst weergegeven worden. Voor overige vragen, mail ons op
                maaltijdservice@uitgekookt.nl. Er zijn maximaal 150 tekens in te voeren. Voor het
                plaatsen van een vaste route opmerking, ga naar het tabblad "Profiel".
              </div>
            }
          >
            {form.getFieldDecorator('route_comments', {
              rules: [
                { required: false },
                {
                  max: 150,
                  message: 'De maximale lengte is 150 karakters'
                }
              ],
              initialValue: existingOrder ? existingOrder.route_comments : undefined
            })(<TextAreaWithCharacterCount showCharacterCount rows={4} maxLength={150} />)}
          </Form.Item>
        </Col>
        <Col xs={12}>
          <Form.Item labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} label='Vouchercode'>
            {form.getFieldDecorator('voucher_code', {
              rules: [{ required: false }],
              initialValue: existingOrder ? existingOrder.voucher_code : undefined
            })(<Input />)}
          </Form.Item>
        </Col>
      </Row>
      {selectedDeliveryMoment !== null && (
        <>
          {!existingOrder && (
            <Row>
              <div style={{ float: 'right', marginRight: 10 }}>
                <label>Pas een standaardportiegrootte toe: </label>
                <Select<number>
                  style={{ width: 220 }}
                  placeholder={'- Maak een keuze -'}
                  onChange={setDefaultPortionId}
                  value={defaultPortionId || undefined}
                >
                  <Select.Option value={2}>Small (350g)</Select.Option>
                  <Select.Option value={1}>Medium (450g)</Select.Option>
                  <Select.Option value={3}>Large (600g)</Select.Option>
                </Select>
              </div>
            </Row>
          )}
          {selectedDeliveryMoment.categories.map((category, categoryIndex) => {
            return (
              <React.Fragment key={categoryIndex}>
                <h3 style={{ margin: 0, fontWeight: 700 }}>{category.name}</h3>
                <Form.Item style={{ marginBottom: 0 }} {...dishLayout}>
                  {category.dishes.map((dish, dishIndex) => {
                    const hasOnePortion = dish.portions.length === 1;
                    const key = `dishes[${dish.camasysId}]`;
                    const selectedKey = `${key}.selected`;
                    const camasysIdKey = `${key}.camasysid`;

                    const initialDishSelectedValue = existingOrder
                      ? existingOrder.dishes.some(
                          (existingDish) => existingDish.dish_id === dish.camasysId
                        )
                      : false;

                    return (
                      <Row gutter={5} key={dishIndex}>
                        <Col span={0}>
                          <Form.Item>
                            {form.getFieldDecorator(camasysIdKey, {
                              initialValue: dish.camasysId
                            })(<InputNumber type='hidden' />)}
                          </Form.Item>
                        </Col>
                        <Col span={16}>
                          <Form.Item style={{ marginBottom: 0 }}>
                            {form.getFieldDecorator(selectedKey, {
                              valuePropName: 'checked',
                              initialValue: initialDishSelectedValue
                            })(
                              <Checkbox>
                                <Tag color='#ff9800'>{dish.dishNumber}</Tag>
                                &nbsp;{dish.name}
                                &nbsp;&nbsp;
                              </Checkbox>
                            )}
                          </Form.Item>
                        </Col>

                        {form.getFieldValue(selectedKey) && (
                          <Col span={8}>
                            {dish.portions.map((portion, portionIndex) => {
                              let initialPortionValue: number | undefined = undefined;

                              if (existingOrder) {
                                const existingPortion = existingOrder.dishes.find(
                                  (existingDish) =>
                                    existingDish.dish_id === dish.camasysId &&
                                    existingDish.portion === portion.camasysId
                                );

                                if (existingPortion) {
                                  initialPortionValue = existingPortion.amount;
                                }
                              } else {
                                const currentQuantity = form.getFieldValue(
                                  `dishes[${dish.camasysId}].portions[${portion.camasysId}].quantity`
                                );
                                if (currentQuantity === undefined) {
                                  // first time value is entered
                                  if (portion.camasysId === defaultPortionId || hasOnePortion) {
                                    // with default portion selected and matching
                                    initialPortionValue = 1; // set default to 1
                                  } else {
                                    initialPortionValue = 0; // otherwise set default to 0
                                  }
                                } else {
                                  initialPortionValue = currentQuantity; // when value is already entered keep the value
                                }
                              }

                              return (
                                <Row key={portionIndex} type='flex' justify='space-between'>
                                  <Col span={14}>{hasOnePortion ? '' : portion.description}</Col>
                                  <Col span={0}>
                                    <Form.Item>
                                      {form.getFieldDecorator(
                                        `dishes[${dish.camasysId}].portions[${portion.camasysId}].camasysid`,
                                        { initialValue: portion.camasysId }
                                      )(<InputNumber type='hidden' />)}
                                    </Form.Item>
                                  </Col>
                                  <Col span={9}>
                                    <Form.Item style={{ marginBottom: 0 }}>
                                      {form.getFieldDecorator(
                                        `dishes[${dish.camasysId}].portions[${portion.camasysId}].quantity`,
                                        { initialValue: initialPortionValue }
                                      )(<InputNumber type='number' min={0} placeholder='Aantal' />)}
                                    </Form.Item>
                                  </Col>
                                </Row>
                              );
                            })}
                          </Col>
                        )}
                      </Row>
                    );
                  })}
                </Form.Item>
              </React.Fragment>
            );
          })}
        </>
      )}
      <Row>
        <Col>
          <Button onClick={onHideClick}>Annuleren</Button>
          <Button
            style={{ float: 'right' }}
            disabled={selectedDeliveryMoment === null}
            loading={isSubmitting}
            type='primary'
            onClick={onSaveClick}
          >
            Opslaan
          </Button>
        </Col>
      </Row>
    </Form>
  );
};

export { OrderFormComponent };
